import React, { useCallback, useEffect, useState } from 'react';
import { AudioElement, ImageElement, VideoElement } from '@bynder-studio/render-core';
import { useTranslate } from '@bynder/localization';
import useEditor from 'packages/hooks/useEditor';
import PersonalUploadsBase from './PersonalUploadsBase';
import { createOnTogglePersonalUploads, isPersonalUploadsAllowed } from './utils';
import MaybeTooltip from 'packages/common/MaybeTooltip';

type Props = {
    disabled?: boolean;
    selectedElements: (ImageElement | VideoElement | AudioElement)[];
};

const PersonalUploads = ({ selectedElements, disabled = false }: Props) => {
    const { translate } = useTranslate();

    const selectedUnlockedElements = selectedElements.filter((element) => !element.locked);
    const allLocked = selectedElements.every((element) => element.locked);
    const allPersonalUploadsEnabled = isPersonalUploadsAllowed(
        selectedElements.length > 1 && selectedUnlockedElements.length ? selectedUnlockedElements : selectedElements,
    );

    const { creativeModel } = useEditor();
    const [isAllowed, setAllowed] = useState(false);

    const onToggle = useCallback(() => {
        const newAllowedState = !allPersonalUploadsEnabled;

        selectedUnlockedElements.forEach((element) => {
            createOnTogglePersonalUploads(creativeModel, element, newAllowedState);
        });

        setAllowed(newAllowedState);
    }, [creativeModel, selectedUnlockedElements, allPersonalUploadsEnabled]);

    useEffect(() => {
        setAllowed(allPersonalUploadsEnabled);
    }, [allPersonalUploadsEnabled]);

    return (
        <MaybeTooltip
            content={translate('editor.element.multiselect.override.tooltip')}
            hasTooltip={selectedElements.length > 1 && (!allLocked || disabled)}
        >
            <PersonalUploadsBase isAllowed={isAllowed} onToggle={onToggle} disabled={allLocked || disabled} />
        </MaybeTooltip>
    );
};

export default PersonalUploads;
